import type { FilterFieldConfig } from '@pesto/ui';

import {
  createBooleanColumn,
  createColumnConfig,
  createDateColumn,
  createFilterField,
  createQueryFilterField,
  createSortableColumnId,
  createTextColumn,
  useGenericListConfig,
} from '@pesto/ui';
import { DataTableColumnHeader } from '@pesto/ui/components/DataTable/DataTableColumnHeader';
import {
  generateBooleanFilterOptions,
  generateSelectableFilterOptions,
} from '@pesto/ui/features/SmartTable/utils/helpers';

import type { ListCardsQueryVariables } from '../../../__generated__/graphql/api';
import type { CardListItem } from '../toCardListItem';

import { CardStatus } from '../../../__generated__/graphql/api';

const columnIds = {
  cardId: createSortableColumnId('idSearch', 'idSortDirection'),
  referenceId: createSortableColumnId('referenceIdSearch', 'referenceIdSortDirection'),
  status: createSortableColumnId('statusSelections', 'statusSortDirection'),
  active: 'active',
  customer: 'userFirstNameSearch',
  program: 'program',
  insertedAt: createSortableColumnId('', 'insertedAtSortDirection'),
};

export const useCardsListConfig = (programFilterFields: FilterFieldConfig[]) => {
  const filterFields = [
    createFilterField('Card ID', columnIds.cardId, { placeholder: 'Card ID search' }),
    createFilterField('Reference ID', columnIds.referenceId, { placeholder: 'Reference ID' }),
    createFilterField('Status', columnIds.status, {
      options: generateSelectableFilterOptions(CardStatus, false),
    }),
    createFilterField('Customer name', columnIds.customer, { placeholder: 'Customer name' }),
    createFilterField('Active', columnIds.active, {
      options: generateBooleanFilterOptions(),
    }),
    createQueryFilterField<ListCardsQueryVariables>('Friends & Family', 'userIsFriendsAndFam', {
      options: generateBooleanFilterOptions(),
      variant: 'boolean',
    }),
    ...programFilterFields,
  ];

  const columns = [
    createColumnConfig<CardListItem>(
      columnIds.cardId,
      ({ column }) => <DataTableColumnHeader column={column} title="Card ID" />,
      {
        accessorFn: (row) => row.id,
        cellType: 'text',
      },
    ),
    createTextColumn<CardListItem>(columnIds.referenceId, 'Reference ID', (row) => row.referenceId),
    createTextColumn<CardListItem>(columnIds.status, 'Status', (row) => row.status),
    createBooleanColumn<CardListItem>(columnIds.active, 'Active', (row) => row.active),
    createTextColumn<CardListItem>(columnIds.customer, 'Customer', (row) => row.userDisplayName),
    createTextColumn<CardListItem>(columnIds.program, 'Program', (row) => row.programName),
    createDateColumn<CardListItem>(
      columnIds.insertedAt,
      ({ column }) => <DataTableColumnHeader column={column} title="Created" />,
      (row) => row.insertedAt,
    ),
  ];

  return useGenericListConfig<CardListItem>({
    columns,
    filterFields,
    showActionColumn: true,
    actionButtonText: 'View',
    pinnedColumns: ['view'],
  });
};
